import { Alert } from "@gotts.ai/ui/components/Alert";
import { PcbCard } from "@gotts.ai/ui/devtools/components/PcbCard";
import { useCallback, useState } from "react";

import { useRequiredTevmContext } from "../context/TevmContext.js";
import { SCENARIOS } from "../scenarios/index.js";

import type { Scenario, ScenarioContext } from "../scenarios/index.js";

function ScenarioCard({
  scenario,
  onRun,
  isRunning,
  status,
}: {
  scenario: Scenario;
  onRun: (id: string) => void;
  isRunning: boolean;
  status: string | null;
}) {
  return (
    <PcbCard>
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-[var(--color-text)]">
          {scenario.name}
        </h3>
        <span className="badge-position-type">{scenario.category}</span>
      </div>
      <p className="mb-3 text-xs text-[var(--color-text-muted)]">
        {scenario.description}
      </p>
      <div className="flex items-center justify-between">
        <span className="text-xs text-[var(--color-text-muted)]">
          {scenario.estimatedTime}
        </span>
        <button
          onClick={() => {
            onRun(scenario.id);
          }}
          disabled={isRunning}
          className="chip-preset disabled:opacity-50"
        >
          {isRunning ? "Running..." : "Run"}
        </button>
      </div>
      {status && (
        <p className="mt-2 text-xs text-[var(--color-text-dim)]">{status}</p>
      )}
    </PcbCard>
  );
}

export function Scenarios() {
  const ctx = useRequiredTevmContext();
  const [runningId, setRunningId] = useState<string | null>(null);
  const [statuses, setStatuses] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  const handleRun = useCallback(
    async (id: string) => {
      if (!ctx.deployment) return;

      const scenario = SCENARIOS.find((s) => s.id === id);
      if (!scenario) return;

      setRunningId(id);
      setError(null);
      setStatuses((prev) => ({ ...prev, [id]: "Starting..." }));

      const scenarioCtx: ScenarioContext = {
        publicClient: ctx.publicClient,
        walletClient: ctx.walletClient,
        testClient: ctx.testClient,
        deployment: ctx.deployment,
        onProgress: (msg: string) => {
          setStatuses((prev) => ({ ...prev, [id]: msg }));
        },
      };

      try {
        await scenario.execute(scenarioCtx);
        setStatuses((prev) => ({ ...prev, [id]: "Complete" }));
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setError(msg);
        setStatuses((prev) => ({ ...prev, [id]: `Failed: ${msg}` }));
      } finally {
        setRunningId(null);
      }
    },
    [ctx],
  );

  return (
    <div className="space-y-6">
      <div>
        <h2 className="type-section-title">Scenarios</h2>
        <p className="mt-1 text-sm text-[var(--color-text-muted)]">
          Pre-built sequences to populate your environment with realistic
          on-chain activity.
        </p>
      </div>

      {error && (
        <Alert variant="error" title="Error">
          {error}
        </Alert>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {SCENARIOS.map((scenario) => (
          <ScenarioCard
            key={scenario.id}
            scenario={scenario}
            onRun={handleRun}
            isRunning={runningId === scenario.id}
            status={statuses[scenario.id] ?? null}
          />
        ))}
      </div>
    </div>
  );
}
